import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { nomeDaSessao, useSession } from "@/lib/auth";
import { useMe } from "@/lib/plano";
import { useBloqueados, semBloqueados } from "@/lib/bloqueios";
import { sendMessage, watchMessages, type Message } from "@/data/messages";
import { Avatar, Spinner } from "@/ui/primitives";
import { IconBack } from "@/ui/icons";
import { TabBar } from "@/ui/TabBar";

export function Chat() {
  const { cid = "" } = useParams();
  const { user } = useSession();
  const me = useMe();
  const [msgs, setMsgs] = useState<Message[] | null>(null);
  const [texto, setTexto] = useState("");
  // Recado de bloqueado some do bate-papo, como o prato some do feed.
  const bloqueados = useBloqueados();
  const scroll = useRef<HTMLDivElement>(null);
  const primeiraPintura = useRef(true);

  useEffect(() => watchMessages(cid, setMsgs), [cid]);

  /* Rola pro fim na abertura e a cada mensagem nova — mas só se a pessoa
     já estava perto do fim. Rolar à força enquanto alguém lê o histórico
     é a forma mais rápida de deixar um chat insuportável. */
  const visiveis = msgs && semBloqueados(msgs, bloqueados);

  useEffect(() => {
    const el = scroll.current;
    if (!el || !msgs) return;
    const noFim = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (primeiraPintura.current || noFim) {
      el.scrollTop = el.scrollHeight;
      primeiraPintura.current = false;
    }
  }, [msgs]);

  async function enviar() {
    const t = texto.trim();
    if (!t || !user) return;
    setTexto("");
    try {
      await sendMessage(cid, {
        uid: user.uid,
        name: me?.name ?? nomeDaSessao(user),
        photo: me?.photo ?? "",
      }, t);
    } catch {
      setTexto(t);
    }
  }

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title">Bate-papo</div>
        <span className="topbar-btn" />
      </div>

      <div className="screen-body chat-corpo">
        <div className="chat-scroll" ref={scroll}>
          {visiveis === null ? <Spinner /> : visiveis.length === 0 ? (
            <div className="empty">
              Silêncio total. Manda a primeira.
            </div>
          ) : visiveis.map((m, i) => {
            const meu = m.uid === user?.uid;
            const mesmoQueAntes = i > 0 && visiveis[i - 1]!.uid === m.uid;
            return (
              <div key={m.id} className={`msg ${meu ? "mine" : ""}`}>
                <span className="msg-face">
                  {!mesmoQueAntes && !meu && <Avatar name={m.name} photo={m.photo} size="sm" />}
                </span>
                <div className="bubble selecionavel">
                  {!mesmoQueAntes && <div className="who">{m.name}</div>}
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>

        <div className="composer-bar above-tabbar">
          <Avatar name={me?.name ?? nomeDaSessao(user)} photo={me?.photo} size="md" />
          <input value={texto} maxLength={800} placeholder="Mensagem"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void enviar(); }} />
          <button className={`send ${texto.trim() ? "on" : ""}`} onClick={enviar}>Enviar</button>
        </div>
      </div>

      <TabBar cid={cid} />
    </main>
  );
}
